<script setup>
/**
 * RED-310 — the Analüütika drawer (PrimeVue Drawer, right). Owns nothing but
 * the visibility wiring: `?analytics=1` on mount opens it, closing drops the
 * param via history.replaceState so a reload doesn't reopen it.
 *
 * Zone A (conditions) lives here; zone B (result) is
 * `AnalyticsResult` (band table, comparison, summary, state panels).
 */
import { onMounted } from 'vue'
import { useAnalyticsStore } from '@/stores/analyticsStore'
import AnalyticsConditionsForm from './AnalyticsConditionsForm.vue'
import AnalyticsConditionsSummary from './AnalyticsConditionsSummary.vue'
import AnalyticsResult from './AnalyticsResult.vue'

const DEEP_LINK_PARAM = 'analytics'

const store = useAnalyticsStore()

onMounted(() => {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams(window.location.search)
  if (params.get(DEEP_LINK_PARAM) === '1') {
    store.open()
  }
})

const dropDeepLink = () => {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  if (!url.searchParams.has(DEEP_LINK_PARAM)) return
  url.searchParams.delete(DEEP_LINK_PARAM)
  window.history.replaceState(window.history.state, '', url.toString())
}

const onVisibleChange = visible => {
  if (visible) {
    store.open()
  } else {
    store.close()
    dropDeepLink()
  }
}
</script>

<template>
  <Drawer
    :visible="store.isOpen"
    position="right"
    :base-z-index="50"
    class="w-[min(1040px,100vw)] max-md:w-full!"
    data-testid="txn-analytics-drawer"
    @update:visible="onVisibleChange"
  >
    <template #header>
      <div class="flex flex-col">
        <span class="text-xl font-medium text-gray-800">
          {{ $t('messages.transactions.analytics.title') }}
        </span>
        <span class="text-sm text-gray-500">
          {{ $t('messages.transactions.analytics.subtitle') }}
        </span>
      </div>
    </template>

    <div class="flex flex-col gap-6 pb-6">
      <AnalyticsConditionsSummary
        v-if="store.formCollapsed"
        @edit="store.formCollapsed = false"
      />
      <AnalyticsConditionsForm v-else />

      <!-- Zone B "Tulemus": switches on `store.state`, reads `store.result`. -->
      <AnalyticsResult />
    </div>
  </Drawer>
</template>
